import React from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './style.module.less';
import { useShouldShowShippyButton } from './use-should-show-shippy-button';
import { ShippyStaticIcon } from '../../shippy-assistant/shippy-static-icon';
import { useShippySidebar } from '../../shippy-assistant/shippy-sidebar-context';

/**
 * @typedef {object} ShippyButtonProps
 * @property {() => void} onToggle - Callback to toggle sidebar open/closed
 * @property {boolean} isOpen - Whether the sidebar is currently open
 */

/**
 * Shippy AI Assistant button for the top navigation bar
 * @param {ShippyButtonProps} props
 * @returns {React.ReactElement | null}
 */
export const ShippyButton = ({ onToggle, isOpen }) => {
  const intl = useIntl();
  const shouldShowShippyButton = useShouldShowShippyButton();
  const { hasUnreadMessages } = useShippySidebar();

  if (!shouldShowShippyButton) {
    return null;
  }

  const hasUnread = hasUnreadMessages && !isOpen;

  const button = (
    <Button
      ariaExpanded={isOpen}
      ariaLabel={intl.formatMessage(messages.buttonAriaLabel)}
      buttonTitle={hasUnread ? undefined : intl.formatMessage(messages.buttonTitle)}
      className={style.button}
      onClick={() => onToggle()}
      theme={THEME_CONSTANTS['shippy-ai']}
    >
      <ShippyStaticIcon />
      <Text value={messages.buttonText} />
      {hasUnread ? <span className={style.unreadDot} /> : null}
    </Button>
  );

  if (hasUnread) {
    return (
      <Tooltip>
        {button}
        <TooltipContent>
          <Text value={messages.newMessageTooltip} />
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};
